import { PersonMapping } from '@bp/shared-planung-utils';
import { useEffect, useMemo, useState } from 'react';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { XlsxImportEntry } from '../types/XlsxImportTypes';
import styles from './_XlsxImportForms.module.scss';
import { XlsxTeacherMappingFormRow } from './XlsxTeacherMappingFormRow';

type XlsxTeacherMappingFormProps = {
  initialEntries: XlsxImportEntry<PersonMapping>[];
  onEntriesChanged: (entries: XlsxImportEntry<PersonMapping>[]) => void;
};

export const XlsxTeacherMappingForm = ({ initialEntries, onEntriesChanged }: XlsxTeacherMappingFormProps) => {
  const { teacherData } = useLoadBasicData({ pause: false });

  const [entries, setEntries] = useState<XlsxImportEntry<PersonMapping>[]>(initialEntries);

  const profiles = useMemo(
    () => teacherData.people.map((p) => ({ uuid: p.uuid, name: p.displayName })),
    [teacherData.people],
  );

  const availableProfiles: { uuid: string; name: string }[] = useMemo(() => {
    const connectedProfileUuids = entries.map((e) => e.connectProfileUuid).filter((uuid): uuid is string => !!uuid);
    return profiles
      .filter((p) => p && !connectedProfileUuids.includes(p.uuid))
      .map((p) => ({ uuid: p.uuid, name: p.name ?? '' }));
  }, [entries, profiles]);

  useEffect(() => {
    setEntries(initialEntries);
  }, [initialEntries]);

  return (
    <div className={styles['xlsx-mapping-form']}>
      {entries.map((entry, id) => {
        const selectedProfile = profiles.find((p) => p.uuid === entry.connectProfileUuid);

        return (
          <XlsxTeacherMappingFormRow
            key={id}
            id={id}
            entry={entry}
            profiles={availableProfiles}
            selectedProfile={selectedProfile ?? null}
            onProfileSelect={(rowIndex, uuid) => {
              const newEntries: XlsxImportEntry<PersonMapping>[] = entries.map((e) => ({
                ...e,
                connectProfileUuid: e.rowIndex === rowIndex ? uuid : e.connectProfileUuid,
              }));
              setEntries(newEntries);
              onEntriesChanged(newEntries);
            }}
          />
        );
      })}
    </div>
  );
};
